import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { registerAdvertiser } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const RegisterPage = () => {
    const [formData, setFormData] = useState({
        business_name: '',
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        password: '',
        confirm_password: '',
        address: '',
        city: '',
        state: '',
        zip_code: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.password !== formData.confirm_password) {
            toast.error('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            const { confirm_password, ...registerData } = formData;
            const response = await registerAdvertiser(registerData);
            login(response.data.token, response.data.advertiser);
            toast.success('Account created successfully!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className="min-h-screen bg-gray-50 py-12 px-4" data-testid="register-page">
                <div className="max-w-2xl mx-auto">
                    <div className="bg-white rounded-xl shadow-sm p-8">
                        <div className="text-center mb-8">
                            <h1 className="font-heading text-3xl font-bold text-navy mb-2">Create Advertiser Account</h1>
                            <p className="text-gray-600">Join Hotel Motel Club and start posting ads for free</p>
                        </div>

                        {/* Benefits */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
                            <h3 className="font-semibold text-green-800 mb-2">✨ 100% Free Platform</h3>
                            <ul className="space-y-1 text-sm text-green-700">
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4" /> <strong>Unlimited ads</strong> - post as many as you need
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4" /> <strong>No fees</strong> - completely free, forever
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4" /> Full dashboard access & analytics
                                </li>
                            </ul>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Business Info */}
                            <div>
                                <h3 className="font-semibold text-navy mb-4 pb-2 border-b">Business Information</h3>
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="business_name">Business Name *</Label>
                                        <Input
                                            id="business_name"
                                            name="business_name"
                                            value={formData.business_name}
                                            onChange={handleChange}
                                            required
                                            className="mt-1"
                                            data-testid="business-name-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Personal Info */}
                            <div>
                                <h3 className="font-semibold text-navy mb-4 pb-2 border-b">Personal Information</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="first_name">First Name *</Label>
                                        <Input
                                            id="first_name"
                                            name="first_name"
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            required
                                            className="mt-1"
                                            data-testid="first-name-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="last_name">Last Name *</Label>
                                        <Input
                                            id="last_name"
                                            name="last_name"
                                            value={formData.last_name}
                                            onChange={handleChange}
                                            required
                                            className="mt-1"
                                            data-testid="last-name-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="email">Email Address *</Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="mt-1"
                                            data-testid="email-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="phone">Phone Number *</Label>
                                        <Input
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            required
                                            className="mt-1"
                                            data-testid="phone-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Address */}
                            <div>
                                <h3 className="font-semibold text-navy mb-4 pb-2 border-b">Address</h3>
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="address">Street Address *</Label>
                                        <Input
                                            id="address"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            required
                                            className="mt-1"
                                            data-testid="address-input"
                                        />
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-4">
                                        <div>
                                            <Label htmlFor="city">City *</Label>
                                            <Input
                                                id="city"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                required
                                                className="mt-1"
                                                data-testid="city-input"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="state">State *</Label>
                                            <Input
                                                id="state"
                                                name="state"
                                                value={formData.state}
                                                onChange={handleChange}
                                                required
                                                className="mt-1"
                                                data-testid="state-input"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="zip_code">ZIP Code *</Label>
                                            <Input
                                                id="zip_code"
                                                name="zip_code"
                                                value={formData.zip_code}
                                                onChange={handleChange}
                                                required
                                                className="mt-1"
                                                data-testid="zip-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Password */}
                            <div>
                                <h3 className="font-semibold text-navy mb-4 pb-2 border-b">Security</h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="password">Password *</Label>
                                        <div className="relative mt-1">
                                            <Input
                                                id="password"
                                                name="password"
                                                type={showPassword ? 'text' : 'password'}
                                                value={formData.password}
                                                onChange={handleChange}
                                                required
                                                minLength={6}
                                                className="pr-10"
                                                data-testid="password-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="confirm_password">Confirm Password *</Label>
                                        <Input
                                            id="confirm_password"
                                            name="confirm_password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.confirm_password}
                                            onChange={handleChange}
                                            required
                                            className="mt-1"
                                            data-testid="confirm-password-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-gold hover:bg-amber-600"
                                disabled={loading}
                                data-testid="register-btn"
                            >
                                {loading ? 'Creating Account...' : 'Create Free Account'}
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <p className="text-gray-600">
                                Already have an account?{' '}
                                <Link to="/advertiser/login" className="text-gold hover:underline font-medium" data-testid="login-link">
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default RegisterPage;
